// S-2229: Enhanced error types for pose extraction payload validation
export enum AnalysisErrorType {
  // Existing error types
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTH_ERROR = 'AUTH_ERROR',
  VIDEO_NOT_READY = 'VIDEO_NOT_READY',
  INVALID_REQUEST = 'INVALID_REQUEST',
  TRANSIENT_ERROR = 'TRANSIENT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  
  // S-2229: New client payload validation error
  CLIENT_PAYLOAD_INVALID = 'CLIENT_PAYLOAD_INVALID',
  
  // S-2237: New watchdog error for CORS/network failures
  NETWORK_OR_CORS_FAILURE = 'NETWORK_OR_CORS_FAILURE',
  
  // S-2243: New error types for analyze-chunk v2 contract
  ANALYSIS_INPUT_MISSING = 'ANALYSIS_INPUT_MISSING',
  MODEL_REJECTED = 'MODEL_REJECTED',
  RETRYABLE_UPSTREAM = 'RETRYABLE_UPSTREAM'
}

export class AnalysisError extends Error {
  public readonly type: AnalysisErrorType;
  public readonly retryable: boolean;
  public readonly userMessage: string;
  public readonly context: Record<string, any>;

  constructor(
    type: AnalysisErrorType,
    message: string,
    userMessage: string,
    retryable: boolean = true,
    context: Record<string, any> = {}
  ) {
    super(message);
    this.name = 'AnalysisError';
    this.type = type;
    this.userMessage = userMessage;
    this.retryable = retryable;
    this.context = context;
    Object.setPrototypeOf(this, AnalysisError.prototype);
  }

  // Existing factory methods
  static networkError(originalError: any): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.NETWORK_ERROR,
      'Network connection failed',
      'Please check your internet connection and try again',
      true,
      { originalError: String(originalError) }
    );
  }

  static authError(message?: string): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.AUTH_ERROR,
      message || 'Authentication failed',
      'Please sign in again to continue',
      false,
      {}
    );
  }

  static videoNotReady(message?: string): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.VIDEO_NOT_READY,
      message || 'Video not ready for analysis',
      'Video is still processing. Please wait and try again.',
      true,
      {}
    );
  }

  static invalidRequest(message?: string): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.INVALID_REQUEST,
      message || 'Invalid request',
      'This video cannot be analyzed. It may have been deleted or is no longer available.',
      false,
      {}
    );
  }

  static transientError(message?: string): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.TRANSIENT_ERROR,
      message || 'Temporary service error',
      'Service temporarily unavailable. Please try again in a moment.',
      true,
      {}
    );
  }

  static unknown(originalError: any): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.UNKNOWN_ERROR,
      'An unexpected error occurred',
      'Something went wrong. Please try again.',
      true,
      { originalError: String(originalError) }
    );
  }

  // S-2229: New factory method for client payload validation errors
  static clientPayloadInvalid(field: string, remediation: string): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.CLIENT_PAYLOAD_INVALID,
      `Missing or invalid required field: ${field}`,
      remediation,
      false, // Don't retry payload errors - they need user action
      { field, remediation }
    );
  }

  // S-2237: New factory method for CORS/network watchdog failures
  static networkOrCorsFailure(runId: string, chunkNumber: number, originalError: any): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.NETWORK_OR_CORS_FAILURE,
      `Chunked analysis aborted after first failure at chunk ${chunkNumber}`,
      'Analysis failed due to network or CORS issue. Please refresh the page and try again.',
      false, // Don't retry - user needs to refresh
      { runId, chunkNumber, originalError: String(originalError) }
    );
  }

  // S-2243: New factory methods for analyze-chunk v2 contract
  static inputMissing(field: string, userMessage: string): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.ANALYSIS_INPUT_MISSING,
      `Missing required analysis input: ${field}`,
      userMessage,
      false, // Not retryable - missing data
      { field }
    );
  }

  static modelRejected(reason: string): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.MODEL_REJECTED,
      `Analysis model rejected request: ${reason}`,
      'The analysis service couldn\'t process this payload. Try trimming length or re-recording with clearer motion.',
      false, // Not retryable - model rejection
      { reason }
    );
  }

  static retryable(reason: string): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.RETRYABLE_UPSTREAM,
      `Upstream service temporarily unavailable: ${reason}`,
      'Analysis service busy. Retrying...',
      true, // Retryable
      { reason }
    );
  }

  // Missing quota exceeded method referenced in Analyze.tsx
  static quotaExceeded(message?: string): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.INVALID_REQUEST,
      message || 'Analysis quota exceeded',
      'You have reached your monthly analysis limit. Please upgrade or wait until next month.',
      false,
      {}
    );
  }

  // Missing model error method referenced in Analyze.tsx
  static modelError(message?: string): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.UNKNOWN_ERROR,
      message || 'Analysis model error',
      'The analysis service encountered an error. Please try again.',
      true,
      {}
    );
  }

  // Missing lowFrames method referenced in tests
  static lowFrames(framesCaptured: number, minRequired: number): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.CLIENT_PAYLOAD_INVALID,
      `Insufficient frames: ${framesCaptured} captured, ${minRequired} required`,
      `Video too short for analysis. Need at least ${minRequired} frames but only captured ${framesCaptured}.`,
      false,
      { framesCaptured, minRequired }
    );
  }

  // Missing missingTelemetry method referenced in tests
  static missingTelemetry(message?: string): AnalysisError {
    return new AnalysisError(
      AnalysisErrorType.ANALYSIS_INPUT_MISSING,
      message || 'Missing telemetry data',
      'No pose data detected in video. Please ensure the golfer is clearly visible and try again.',
      false,
      { field: 'telemetry' }
    );
  }
}

// Helper function to classify generic errors
export function classifyAnalysisError(error: any): AnalysisError {
  if (error instanceof AnalysisError) {
    return error;
  }

  const errorMessage = error?.message || String(error);
  const lowerMessage = errorMessage.toLowerCase();

  if (lowerMessage.includes('network') || lowerMessage.includes('fetch')) {
    return AnalysisError.networkError(error);
  }

  if (lowerMessage.includes('auth') || lowerMessage.includes('unauthorized') || lowerMessage.includes('forbidden')) {
    return AnalysisError.authError(errorMessage);
  }

  if (lowerMessage.includes('not found') || lowerMessage.includes('missing')) {
    return AnalysisError.invalidRequest(errorMessage);
  }

  return AnalysisError.unknown(error);
}

// S-2229: Helper to classify video details errors for analysis context
export function classifyVideoDetailsError(error: any): AnalysisError {
  if (error instanceof AnalysisError) {
    return error;
  }

  if (error?.code) {
    switch (error.code) {
      case 'CLIENT_ERROR':
      case 'VIDEO_NOT_FOUND':
        return AnalysisError.invalidRequest(error.message);
      case 'PROCESSING_INCOMPLETE':
        return AnalysisError.videoNotReady(error.message);
      case 'AUTH_ERROR':
        return AnalysisError.authError('Authentication required - please sign in again');
      case 'NETWORK_ERROR':
        return AnalysisError.networkError(error);
      case 'DATABASE_ERROR':
        return AnalysisError.transientError(error.message);
      default:
        return AnalysisError.unknown(error);
    }
  }

  return classifyAnalysisError(error);
}

// S-2237: Helper function to detect CORS/network failures for watchdog
export function isNetworkOrCorsError(error: any): boolean {
  if (!error) return false;
  
  const errorMessage = error?.message || String(error);
  const lowerMessage = errorMessage.toLowerCase();
  
  // Detect CORS errors
  if (lowerMessage.includes('cors') || 
      lowerMessage.includes('cross-origin') ||
      lowerMessage.includes('preflight') ||
      lowerMessage.includes('blocked by cors policy')) {
    return true;
  }
  
  // Detect network errors
  if (error instanceof TypeError && 
      (lowerMessage.includes('failed to fetch') || 
       lowerMessage.includes('network error') ||
       lowerMessage.includes('fetch failed'))) {
    return true;
  }
  
  // Detect connection errors
  if (lowerMessage.includes('connection refused') ||
      lowerMessage.includes('connection timeout') ||
      lowerMessage.includes('network request failed') ||
      lowerMessage.includes('unable to connect')) {
    return true;
  }
  
  return false;
}

// S-2237: Helper function to check if error is retryable (429, 503, etc.)
export function isRetryableError(error: any): boolean {
  if (!error) return false;
  
  const errorMessage = error?.message || String(error);
  
  // Check for HTTP status codes that are retryable
  if (errorMessage.includes('429') || errorMessage.includes('503') || errorMessage.includes('502')) {
    return true;
  }
  
  // Check for temporary service errors
  const lowerMessage = errorMessage.toLowerCase();
  if (lowerMessage.includes('rate limit') ||
      lowerMessage.includes('service unavailable') ||
      lowerMessage.includes('temporarily unavailable') ||
      lowerMessage.includes('server overloaded')) {
    return true;
  }
  
  return false;
}